/**
 * Illustrated clay art for meal types, food placeholders, entry sources and
 * quick-add modes — URL mirror of mobile constants/meal-icons.ts (same names,
 * same keys; files under public/icons/ = mobile assets/icons/).
 *
 * Each PNG carries its own emerald / cream, so it is rendered bare — never
 * tinted, and never sat on a coloured circle.
 */

import type { MealTime } from '@/types'

export const MEAL_IMAGES: Record<MealTime, string> = {
  breakfast: '/icons/meals/breakfast.png',
  lunch: '/icons/meals/lunch.png',
  dinner: '/icons/meals/dinner.png',
  snack: '/icons/meals/snack.png',
}

export function mealImage(mealType?: string): string {
  return MEAL_IMAGES[mealType as MealTime] ?? MEAL_IMAGES.snack
}

/** Empty-state / missing-photo art. */
export const FOOD_PLACEHOLDER_IMAGES = {
  meal: '/icons/placeholders/food-meal.png',
  drink: '/icons/placeholders/food-drink.png',
  packaged: '/icons/placeholders/food-packaged.png',
  produce: '/icons/placeholders/food-produce.png',
  recipe: '/icons/placeholders/food-recipe.png',
  snack: '/icons/placeholders/food-snack.png',
} as const

/** Quick-add sheet / scan-mode art. */
export const QUICK_ADD_IMAGES = {
  scanMeal: '/icons/quickadd/scan-meal.png',
  label: '/icons/quickadd/mode-label.png',
  barcode: '/icons/quickadd/mode-barcode.png',
  voiceText: '/icons/quickadd/voice-text.png',
  manual: '/icons/quickadd/manual-entry.png',
  recent: '/icons/quickadd/recent.png',
  recipes: '/icons/quickadd/recipes.png',
  savedFoods: '/icons/quickadd/saved-foods.png',
} as const

/** Where a diary entry came from (entry rows without a photo). */
export const SOURCE_IMAGES: Record<string, string> = {
  scan: '/icons/quickadd/scan-meal.png',
  recipe: '/icons/quickadd/recipes.png',
  barcode: '/icons/quickadd/mode-barcode.png',
  search: '/icons/sources/source-search.png',
  fluid: '/icons/fluids/water.png',
  manual: '/icons/quickadd/manual-entry.png',
  quick_log: '/icons/quickadd/voice-text.png',
  copied: '/icons/sources/source-copied.png',
  healthkit: '/icons/sources/source-healthkit.png',
  favorite: '/icons/quickadd/saved-foods.png',
}

/** Home-screen quick-log tiles. */
export const TILE_IMAGES = {
  weight: '/icons/tiles/tile-weight.png',
  bloodPressure: '/icons/tiles/tile-blood-pressure.png',
  notes: '/icons/analysis/menu-notes.png',
} as const

/** Streak art (flame + milestone medals). */
export const STREAK_IMAGES = {
  flame: '/icons/streak/flame.png',
  3: '/icons/streak/milestone-3.png',
  7: '/icons/streak/milestone-7.png',
  14: '/icons/streak/milestone-14.png',
  30: '/icons/streak/milestone-30.png',
  60: '/icons/streak/milestone-60.png',
  100: '/icons/streak/milestone-100.png',
} as const

/** Medal for the highest milestone reached (falls back to the flame). */
export function streakMilestoneImage(days: number): string {
  const tiers = [100, 60, 30, 14, 7, 3] as const
  const hit = tiers.find((d) => days >= d)
  return hit ? STREAK_IMAGES[hit] : STREAK_IMAGES.flame
}

/** Generic condition / diet art (organ-neutral subjects only). */
export const CONDITION_IMAGES = {
  diabetes: '/icons/conditions/comorbid-diabetes.png',
  heartDisease: '/icons/conditions/comorbid-heart-disease.png',
  dairyFree: '/icons/conditions/diet-dairy-free.png',
  glutenFree: '/icons/conditions/diet-gluten-free.png',
  vegan: '/icons/conditions/diet-vegan.png',
  vegetarian: '/icons/conditions/diet-vegetarian.png',
  healthy: '/icons/conditions/healthy.png',
  notSure: '/icons/conditions/not-sure.png',
} as const
